"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StopsOnRouteRequestSchema = exports.RouteViaStopQuerySchema = exports.RouteWithRestAreasQuerySchema = exports.RouteQuerySchema = exports.RouteFilterParamsSchema = exports.FilterOptionsSchema = void 0;
const zod_1 = require("zod");
const geo_1 = require("./geo");
const rest_area_1 = require("./rest-area");
const routing_1 = require("./routing");
// Request parameters of the routing endpoints. Query strings and the stop-search body carry
// filters as strings ("a,b" lists, "true"/"false"), because deployed apps send them that way.
const DataOriginSchema = zod_1.z.nativeEnum(rest_area_1.DataOrigin);
exports.FilterOptionsSchema = zod_1.z.object({
    availabilityStatuses: zod_1.z.array(rest_area_1.SiteAvailabilityStatusSchema).optional(),
    arrivalAvailabilityStatuses: zod_1.z.array(rest_area_1.SiteAvailabilityStatusSchema).optional(),
    /** Null when a max price was sent but isn't a number. */
    maxPrice: zod_1.z.number().nullable().optional(),
    hasBooking: zod_1.z.boolean().optional(),
    freeOfCharge: zod_1.z.boolean().optional(),
    selectedAmenities: zod_1.z.array(zod_1.z.string()).optional(),
    selectedOrigins: zod_1.z.array(DataOriginSchema).optional(),
});
function parseKnownEntries(value, schema) {
    return value.split(',').flatMap((entry) => {
        const parsed = schema.safeParse(entry.trim());
        return parsed.success ? [parsed.data] : [];
    });
}
/** Unknown statuses are dropped rather than rejected, so an older client's list still filters. */
const statusListParamSchema = zod_1.z
    .string()
    .nullish()
    .transform((value) => value == null ? undefined : parseKnownEntries(value, rest_area_1.SiteAvailabilityStatusSchema));
const booleanParamSchema = zod_1.z
    .string()
    .nullish()
    .transform((value) => value == null ? undefined : value.toLowerCase() === 'true');
const numberParamSchema = zod_1.z
    .string()
    .nullish()
    .transform((value) => {
    if (value == null)
        return undefined;
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
});
const routeFilterQueryParamsSchema = zod_1.z.object({
    availabilityStatuses: statusListParamSchema,
    arrivalAvailabilityStatuses: statusListParamSchema,
    maxPrice: numberParamSchema,
    hasBooking: booleanParamSchema,
    freeOfCharge: booleanParamSchema,
    amenities: zod_1.z
        .string()
        .nullish()
        .transform((value) => value ? parseKnownEntries(value, zod_1.z.string().min(1)) : undefined),
    origins: zod_1.z
        .string()
        .nullish()
        .transform((value) => value == null ? undefined : parseKnownEntries(value, DataOriginSchema)),
});
exports.RouteFilterParamsSchema = routeFilterQueryParamsSchema.transform(({ amenities, origins, ...filters }) => ({
    ...filters,
    selectedAmenities: amenities,
    selectedOrigins: origins,
}));
/** `lng,lat` as sent in a query string. */
const queryCoordSchema = zod_1.z
    .string()
    .transform((value) => value.split(',').map((part) => Number(part.trim())))
    .pipe(geo_1.coordTupleSchema);
const bodyCoordSchema = zod_1.z.union([queryCoordSchema, geo_1.coordTupleSchema]);
exports.RouteQuerySchema = zod_1.z.object({
    start: queryCoordSchema,
    end: queryCoordSchema,
});
exports.RouteWithRestAreasQuerySchema = exports.RouteQuerySchema.and(exports.RouteFilterParamsSchema);
exports.RouteViaStopQuerySchema = exports.RouteQuerySchema.extend({
    waypoint: queryCoordSchema,
    avoidTolls: booleanParamSchema.transform((value) => value ?? false),
});
const stopsOnRouteBodySchema = zod_1.z.object({
    start: bodyCoordSchema,
    end: bodyCoordSchema,
    remainingTime: routing_1.RemainingTimeSecondsSchema,
    waypoints: zod_1.z.array(bodyCoordSchema).max(routing_1.MAX_WAYPOINTS).default([]),
    avoidTolls: zod_1.z
        .union([
        zod_1.z.boolean(),
        zod_1.z.string().transform((value) => value.toLowerCase() === 'true'),
    ])
        .default(false),
    /** Deployed apps don't send it and still read geometry and rest-area details from every stop. */
    includeStopDetails: zod_1.z.boolean().default(true),
});
/** `getRoutesWithStopOnRoute` body; the filters sit flat next to the route fields. */
exports.StopsOnRouteRequestSchema = stopsOnRouteBodySchema.and(exports.RouteFilterParamsSchema);
//# sourceMappingURL=routeRequests.js.map